/**
 * "+ Sorgente" entry point (ADR-0026/0027) — one button, a typed choice.
 *
 * Instead of a separate button per source kind (which doesn't scale as kinds
 * grow), the page opens THIS chooser: a small modal of clickable boxes, one per
 * source kind. Picking a box closes the chooser and the parent opens the right
 * flow (file upload / SQL connection). Add a kind = one entry in `KINDS`.
 */
import { Database, FileSpreadsheet, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/cn";

/** The flows a new source can start. Grows as new source protocols land. */
export type SourceChoice = "file" | "sql";

interface KindSpec {
  choice: SourceChoice;
  icon: LucideIcon;
  titleKey: string;
  titleFallback: string;
  descKey: string;
  descFallback: string;
}

const KINDS: KindSpec[] = [
  {
    choice: "file",
    icon: FileSpreadsheet,
    titleKey: "sources.chooser.file.title",
    titleFallback: "CSV / Excel",
    descKey: "sources.chooser.file.desc",
    descFallback: "Carica un file: le righe vengono importate in Tray come sorgente.",
  },
  {
    choice: "sql",
    icon: Database,
    titleKey: "sources.chooser.sql.title",
    titleFallback: "Database SQL",
    descKey: "sources.chooser.sql.desc",
    descFallback: "Collega un database esterno (Postgres, MySQL, SQL Server) in sola lettura.",
  },
];

export interface SourceKindChooserProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired with the picked kind; the parent opens the matching flow. */
  onPick: (choice: SourceChoice) => void;
}

export function SourceKindChooser({ open, onOpenChange, onPick }: SourceKindChooserProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {t("sources.chooser.title", "Nuova sorgente")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-xs text-muted-foreground">
            {t("sources.chooser.hint", "Da dove arrivano i dati?")}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {KINDS.map((k) => {
              const Icon = k.icon;
              return (
                <button
                  key={k.choice}
                  type="button"
                  onClick={() => onPick(k.choice)}
                  className={cn(
                    "group flex flex-col items-start gap-2 rounded-lg border border-border bg-surface p-4 text-left",
                    "transition-colors hover:border-accent/60 hover:bg-accent/5",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                  )}
                >
                  <Icon
                    className="h-6 w-6 text-muted-foreground transition-colors group-hover:text-accent"
                    strokeWidth={1.5}
                  />
                  <div className="text-sm font-medium">{t(k.titleKey, k.titleFallback)}</div>
                  <div className="text-xs text-muted-foreground">{t(k.descKey, k.descFallback)}</div>
                </button>
              );
            })}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
